import { forwardRef } from 'react';
import { PersonsV3 } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import {
  ElementTypePartial,
  PolymorphicRef,
  PopoverMenuItem,
  PopoverMenuItemProps,
  Text,
  useTooltip,
} from '@frontend/design-system';

type PersonPhoneSelectorItemProps<C extends ElementTypePartial = 'button'> = {
  personPhone: string;
  phoneLabel?: string;
  locationId: string;
} & Omit<PopoverMenuItemProps<C>, 'children' | 'phoneLabel' | 'locationId' | 'personPhone' | 'href'>;

export const PersonPhoneSelectorItem = forwardRef(
  <C extends ElementTypePartial = 'button'>(
    { personPhone, phoneLabel, locationId, ...rest }: PersonPhoneSelectorItemProps<C>,
    ref?: PolymorphicRef<C>
  ) => {
    const { t } = useTranslation('thread-header');
    const { data: contactsCount = 0 } = PersonsV3.PersonQueries.useListPreferredPersonsByPhoneQuery(
      {
        phoneNumber: personPhone,
        locationId,
      },
      { select: (data) => data.persons.length }
    );

    const { Tooltip, triggerProps, tooltipProps } = useTooltip({
      placement: 'bottom',
      trigger: 'hover',
    });

    return (
      <PopoverMenuItem {...rest} ref={ref}>
        {`${phoneLabel ?? ''} ${formatPhoneNumber(personPhone)}`.trim()}
        {contactsCount > 1 && (
          <>
            <div {...triggerProps} css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name='family-small' color='subdued' />
              <Text size='small' color='subdued'>
                {contactsCount}
              </Text>
            </div>
            <Tooltip {...tooltipProps}>{t('There are multiple contacts listed under this number')}</Tooltip>
          </>
        )}
      </PopoverMenuItem>
    );
  }
);
PersonPhoneSelectorItem.displayName = 'PersonPhoneSelectorItem';
